// Multi-tenant SignalR context for connecting to API hubs via the Portal Backend proxy.

import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import * as signalR from "@microsoft/signalr";
import { getAuthProfile } from "../utils/validation";
import { serverGet } from "../../middleware/http";
import { apiConst } from "../constant/constant";

const HUB_CONFIG = {
  notification: { url: "/hub/notification", requiresAuth: false },
  schedule: { url: "/hub/schedule", requiresAuth: true },
  patient: { url: "/hub/patient", requiresAuth: true },
  message: { url: "/hub/message", requiresAuth: true },
};

export const TenantSignalRContext = createContext({
  connections: {},
  connectionState: "Disconnected",
  invokeHubMethod: () => {},
  stopAll: () => {},
});

export const TenantSignalRProvider = ({
  children,
  hubNames = ["notification"],
}) => {
  const [connections, setConnections] = useState({});
  const [connectionState, setConnectionState] = useState("Disconnected");
  const connectionsRef = useRef({});
  // Each hub's start() promise, so calls made before it connects can wait rather than be dropped.
  const startPromisesRef = useRef({});
  // Message-hub presence scopes, re-joined after a reconnect (which assigns a new id with no groups).
  const activePresenceScopesRef = useRef(new Set());

  const stableHubNames = useMemo(() => hubNames, [JSON.stringify(hubNames)]);

  // Returns whether the user is authenticated, fetching the profile if not already cached.
  const checkAuth = useCallback(async () => {
    let authRes = getAuthProfile.getProfileAuth();

    if (authRes == null || authRes == undefined) {
      try {
        const res = await serverGet(apiConst.getauthprofile);
        if (res?.data?.auth) getAuthProfile.saveProfileAuth();
        authRes = res?.data?.auth;
      } catch {
        return false;
      }
    }

    return authRes && authRes !== "false";
  }, []);

  // Creates and starts a SignalR connection for a hub.
  const createConnection = useCallback((hubName, hubUrl) => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();

    connection.onreconnecting(() => setConnectionState("Reconnecting"));
    connection.onreconnected(() => {
      setConnectionState("Connected");
      // Re-join presence scopes lost on reconnect.
      if (hubName === "message") {
        activePresenceScopesRef.current.forEach((scope) => {
          connection
            .invoke("JoinPresence", scope)
            .catch((err) => console.error("JoinPresence re-join failed:", err));
        });
      }
    });
    connection.onclose(() => setConnectionState("Disconnected"));

    startPromisesRef.current[hubName] = connection
      .start()
      .then(() => {
        setConnectionState("Connected");
      })
      .catch((err) => {
        console.error(`SignalR ${hubName} connection error:`, err);
      });

    return connection;
  }, []);

  // Stops all current connections.
  const stopAll = useCallback(() => {
    Object.values(connectionsRef.current).forEach((conn) => {
      if (conn.state !== signalR.HubConnectionState.Disconnected) {
        conn.stop();
      }
    });
    connectionsRef.current = {};
  }, []);

  useEffect(() => {
    const nonAuthHubs = stableHubNames.filter(
      (name) => HUB_CONFIG[name] && !HUB_CONFIG[name].requiresAuth,
    );

    const newConnections = {};
    nonAuthHubs.forEach((hubName) => {
      newConnections[hubName] = createConnection(
        hubName,
        HUB_CONFIG[hubName].url,
      );
    });

    connectionsRef.current = { ...connectionsRef.current, ...newConnections };
    setConnections((prev) => ({ ...prev, ...newConnections }));

    return () => stopAll();
  }, [stableHubNames, createConnection, stopAll]);

  useEffect(() => {
    const authHubs = stableHubNames.filter(
      (name) => HUB_CONFIG[name] && HUB_CONFIG[name].requiresAuth,
    );

    if (authHubs.length === 0) return;

    const connectAuthHubs = async () => {
      const authenticated = await checkAuth();
      if (!authenticated) return;

      const newConnections = {};
      authHubs.forEach((hubName) => {
        if (connectionsRef.current[hubName]) return;
        newConnections[hubName] = createConnection(
          hubName,
          HUB_CONFIG[hubName].url,
        );
      });

      if (Object.keys(newConnections).length > 0) {
        connectionsRef.current = {
          ...connectionsRef.current,
          ...newConnections,
        };
        setConnections((prev) => ({ ...prev, ...newConnections }));
      }
    };

    connectAuthHubs();
  }, [stableHubNames, checkAuth, createConnection]);

  // Invokes a hub method, waiting for the connection to finish starting first.
  const invokeHubMethod = useCallback(
    async (methodName, args = [], hubName = "notification") => {
      const conn = connectionsRef.current[hubName];
      if (!conn) return;
      // Track presence scopes for reconnect re-join.
      if (hubName === "message" && args[0]) {
        if (methodName === "JoinPresence") activePresenceScopesRef.current.add(args[0]);
        else if (methodName === "LeavePresence") activePresenceScopesRef.current.delete(args[0]);
      }
      try {
        if (conn.state !== signalR.HubConnectionState.Connected) {
          await startPromisesRef.current[hubName];
        }
        if (conn.state === signalR.HubConnectionState.Connected) {
          await conn.invoke(methodName, ...args);
        }
      } catch (err) {
        console.error(`${methodName} failed:`, err);
      }
    },
    [],
  );

  return (
    <TenantSignalRContext.Provider
      value={{ connections, connectionState, invokeHubMethod, stopAll }}
    >
      {children}
    </TenantSignalRContext.Provider>
  );
};
